import React, { useState, useMemo } from 'react';
import { X, Sparkles, Loader2, CheckCircle, ChevronDown } from 'lucide-react';
import { apiClient } from '../services/api';
import { ArrangementGroup } from '../types/api';
import { trackArrangeWithAIApplied } from '../utils/analytics';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface Props {
  categoryId: number;
  categoryName: string;
  leafCards: { id: number; name: string }[];
  onClose: () => void;
  onApplied: () => void;
}

type Step = 'prompt' | 'loading' | 'reviewing' | 'applying' | 'done';

interface EditableGroup {
  folder_name: string;
  card_ids: number[];
}

export const ArrangeWithAIModal: React.FC<Props> = ({ categoryId, categoryName, leafCards, onClose, onApplied }) => {
  useBodyScrollLock();
  const [step, setStep] = useState<Step>('prompt');
  const [prompt, setPrompt] = useState('');
  const [groups, setGroups] = useState<EditableGroup[]>([]);
  const [result, setResult] = useState<{ folders_created: number; cards_moved: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openMoveMenu, setOpenMoveMenu] = useState<{ groupIdx: number; cardId: number } | null>(null);

  const cardNames = useMemo(
    () => Object.fromEntries(leafCards.map(c => [c.id, c.name])),
    [leafCards]
  );

  const handlePropose = async () => {
    setError(null);
    setStep('loading');
    try {
      const data = await apiClient.proposeArrangement(categoryId, prompt.trim() || undefined);
      setGroups(data.groups.map(g => ({ ...g })));
      setStep('reviewing');
    } catch {
      setError('Could not generate a proposal. Please try again.');
      setStep('prompt');
    }
  };

  const handleApply = async () => {
    setStep('applying');
    setError(null);
    try {
      const payload: ArrangementGroup[] = groups.map(g => ({
        folder_name: g.folder_name,
        card_ids: g.card_ids,
      }));
      const res = await apiClient.applyArrangement(categoryId, payload);
      setResult(res);
      setStep('done');
      trackArrangeWithAIApplied();
      onApplied();
    } catch {
      setError('Could not apply the arrangement. Please try again.');
      setStep('reviewing');
    }
  };

  const renameGroup = (idx: number, name: string) => {
    setGroups(prev => prev.map((g, i) => i === idx ? { ...g, folder_name: name } : g));
  };

  const moveCard = (cardId: number, fromGroupIdx: number, toGroupIdx: number) => {
    setGroups(prev => prev.map((g, i) => {
      if (i === fromGroupIdx) return { ...g, card_ids: g.card_ids.filter(id => id !== cardId) };
      if (i === toGroupIdx) return { ...g, card_ids: [...g.card_ids, cardId] };
      return g;
    }));
    setOpenMoveMenu(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              Arrange with AI
            </h2>
            <p className="text-xs text-gray-400 mt-0.5 truncate">&quot;{categoryName}&quot;</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* Step: prompt */}
          {(step === 'prompt' || step === 'loading') && (
            <>
              <p className="text-sm text-gray-500">
                The AI will group your cards into folders. You can guide it with an optional prompt.
              </p>
              <textarea
                className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-purple-300"
                rows={3}
                placeholder="e.g. group by formality level, topic, difficulty…"
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                disabled={step === 'loading'}
              />
              {error && <p className="text-xs text-red-500">{error}</p>}
            </>
          )}

          {/* Step: loading */}
          {step === 'loading' && (
            <div className="flex items-center justify-center py-8 gap-3 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">AI is organising your cards…</span>
            </div>
          )}

          {/* Step: reviewing */}
          {(step === 'reviewing' || step === 'applying') && (
            <>
              <p className="text-sm text-gray-500">
                Review the proposed groups. You can rename folders or move cards between groups.
              </p>
              {groups.map((group, gi) => (
                <div key={gi} className="border border-gray-100 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-100">
                    <input
                      className="w-full text-sm font-semibold bg-transparent focus:outline-none focus:ring-1 focus:ring-purple-300 rounded px-1 -mx-1"
                      value={group.folder_name}
                      onChange={e => renameGroup(gi, e.target.value)}
                      disabled={step === 'applying'}
                    />
                  </div>
                  <ul className="divide-y divide-gray-50">
                    {group.card_ids.map(cardId => (
                      <li key={cardId} className="flex items-center justify-between px-3 py-2">
                        <span className="text-sm text-gray-700 truncate flex-1">
                          {cardNames[cardId] ?? `Card #${cardId}`}
                        </span>
                        {groups.length > 1 && step === 'reviewing' && (
                          <div className="relative flex-shrink-0 ml-2">
                            <button
                              onClick={() => setOpenMoveMenu(
                                openMoveMenu?.cardId === cardId && openMoveMenu.groupIdx === gi ? null : { groupIdx: gi, cardId }
                              )}
                              className="flex items-center gap-0.5 text-xs text-gray-400 hover:text-gray-600 px-1.5 py-1 rounded hover:bg-gray-100 transition-colors"
                            >
                              Move <ChevronDown className="h-3 w-3" />
                            </button>
                            {openMoveMenu?.cardId === cardId && openMoveMenu.groupIdx === gi && (
                              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-36 py-1">
                                {groups.map((g, ti) => ti !== gi && (
                                  <button
                                    key={ti}
                                    onClick={() => moveCard(cardId, gi, ti)}
                                    className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 truncate"
                                  >
                                    {g.folder_name || `Group ${ti + 1}`}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                    {group.card_ids.length === 0 && (
                      <li className="px-3 py-2 text-xs text-gray-400 italic">Empty group</li>
                    )}
                  </ul>
                </div>
              ))}
              {error && <p className="text-xs text-red-500">{error}</p>}
            </>
          )}

          {/* Step: done */}
          {step === 'done' && result && (
            <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
              <CheckCircle className="h-10 w-10 text-green-500" />
              <p className="text-sm font-semibold text-gray-900">Arrangement applied!</p>
              <p className="text-sm text-gray-500">
                {result.folders_created} folder{result.folders_created !== 1 ? 's' : ''} created,{' '}
                {result.cards_moved} card{result.cards_moved !== 1 ? 's' : ''} moved.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-3 border-t border-gray-100 flex-shrink-0 flex gap-2">
          {step === 'prompt' && (
            <>
              <button onClick={onClose} className="flex-1 py-2.5 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={handlePropose}
                className="flex-1 py-2.5 text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Sparkles className="h-4 w-4" /> Generate
              </button>
            </>
          )}
          {step === 'reviewing' && (
            <>
              <button
                onClick={() => { setGroups([]); setStep('prompt'); }}
                className="flex-1 py-2.5 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Start over
              </button>
              <button
                onClick={handleApply}
                disabled={groups.every(g => g.card_ids.length === 0)}
                className="flex-1 py-2.5 text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </>
          )}
          {step === 'applying' && (
            <button disabled className="flex-1 py-2.5 text-sm font-semibold bg-purple-600 text-white rounded-xl opacity-60 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Applying…
            </button>
          )}
          {step === 'done' && (
            <button onClick={onClose} className="flex-1 py-2.5 text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-colors">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
